import { InputNumber } from "@/components/InputNumber";
import { datePickerClearableProps, datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Alert, Col, DatePicker, Form, Input, Modal, Row } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateCommission,
  useUpdateCommission,
  useUpdateDuplicatedCommission,
  useUpdatePostponedCommission,
} from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import LabelWithTooltip from "../../../../../common/components/form/labels/LabelWithTooltip";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import { CommissionKind } from "../../enums";
import { Commission, CreateManuallyAddedCommission, UpdateCommission } from "../../types";
import { CommissionKindSelect } from "../CommissionKindSelect";

const colSpan = 8;

interface Props {
  open: boolean;
  formType: "batchCommission" | "duplicatedCommission" | "postponedCommission";
  commission?: Commission;
  batchId?: UUID;
  institutionId?: UUID;
  onFormCancel: () => void;
}

export const CommissionForm = ({ open, formType, commission, batchId, institutionId, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateManuallyAddedCommission | UpdateCommission>();
  const startDate = Form.useWatch("startDate", form);
  const commissionKind = Form.useWatch("commissionKind", form);
  const isCommissionKindBonus =
    commissionKind === CommissionKind.BONUS || commissionKind === CommissionKind.BONUS_EXTENDED;

  const createCommission = useCreateCommission();
  const updateCommission = useUpdateCommission();
  const updatePostponedCommission = useUpdatePostponedCommission();
  const updateDuplicatedCommission = useUpdateDuplicatedCommission();

  useEffect(() => {
    if (commission && open) {
      form.setFieldsValue({
        contractNumber: commission.contractNumber,
        secondaryContractNumber: commission.secondaryContractNumber,
        tariffCode: commission.tariffCode,
        clientName: commission.clientName,
        startDate: commission.startDate,
        endDate: commission.endDate,
        commissionBaseAmount: commission.commissionBaseAmount,
        commissionAmount: commission.commissionAmount,
        commissionKind: commission.commissionKind,
        optimisticLockVersion: commission.optimisticLockVersion,
      });
      form.validateFields().catch(resolveFormValidationError);
    }

    form.setFieldsValue({ institutionId: institutionId });
  }, [open, commission, institutionId]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        switch (formType) {
          case "batchCommission":
            if (commission && batchId) {
              updateCommission.mutate(
                { id1: batchId, id2: commission.id, object: values as UpdateCommission },
                {
                  onSuccess: onFormCancel,
                  onError: (error) => setErrorsToForm(form, "commissions.batch.attrs.commission", error.violations),
                },
              );
            } else if (batchId && institutionId) {
              createCommission.mutate(
                {
                  id: batchId,
                  object: { ...(values as CreateManuallyAddedCommission), institutionId },
                },
                {
                  onSuccess: onFormCancel,
                  onError: (error) => setErrorsToForm(form, "commissions.batch.attrs.commission", error.violations),
                },
              );
            }
            break;
          case "duplicatedCommission":
            if (batchId && commission?.id) {
              updateDuplicatedCommission.mutate(
                {
                  id1: batchId,
                  id2: commission.id,
                  object: values as UpdateCommission,
                },
                {
                  onSuccess: onFormCancel,
                  onError: (error) => setErrorsToForm(form, "commissions.batch.attrs.commission", error.violations),
                },
              );
            }
            break;
          case "postponedCommission":
            if (commission?.id) {
              updatePostponedCommission.mutate(
                { id: commission.id, object: values as UpdateCommission },
                {
                  onSuccess: onFormCancel,
                  onError: (error) => setErrorsToForm(form, "commissions.batch.attrs.commission", error.violations),
                },
              );
            }
            break;
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.LARGE}
      open={open}
      title={
        commission ? t("commissions.batch.titles.updateCommission") : t("commissions.batch.titles.createCommission")
      }
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={
        updatePostponedCommission.isPending ||
        updateDuplicatedCommission.isPending ||
        createCommission.isPending ||
        updateCommission.isPending
      }
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="commissionUpdateForm">
        <HiddenInput name="optimisticLockVersion" />
        <HiddenInput name="institutionId" />

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="contractNumber"
              label={t("commissions.batch.attrs.commission.contractNumber")}
              dependencies={["secondaryContractNumber"]}
              rules={[
                validations.size(1, 64),
                validations.notNullIfOtherNull(
                  "secondaryContractNumber",
                  t("commissions.batch.attrs.commission.secondaryContractNumber"),
                ),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="secondaryContractNumber"
              label={t("commissions.batch.attrs.commission.secondaryContractNumber")}
              dependencies={["contractNumber"]}
              rules={[
                validations.size(1, 64),
                validations.notNullIfOtherNull(
                  "contractNumber",
                  t("commissions.batch.attrs.commission.contractNumber"),
                ),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="tariffCode"
              label={t("commissions.batch.attrs.commission.tariffCode")}
              rules={[validations.none]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="clientName"
              label={
                isCommissionKindBonus
                  ? t("commissions.batch.helpers.recipient")
                  : t("commissions.batch.attrs.commission.clientName")
              }
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="startDate"
              label={t("commissions.batch.attrs.commission.startDate")}
              rules={[validations.notNull]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerStandardProps} showNow={false} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="endDate"
              label={t("commissions.batch.attrs.commission.endDate")}
              rules={[
                startDate
                  ? validations.notBefore(startDate, t("commissions.batch.attrs.commission.startDate"))
                  : validations.none,
              ]}
              {...datePickerFormItemProps}
            >
              <DatePicker
                {...datePickerClearableProps}
                showNow={false}
                minDate={startDate ? dayjs(startDate) : undefined}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="commissionBaseAmount"
              label={
                formType === "postponedCommission" ? (
                  <LabelWithTooltip
                    label={
                      isCommissionKindBonus
                        ? t("commissions.batch.helpers.nettoPointAmount")
                        : t("commissions.batch.attrs.commission.commissionBaseAmount")
                    }
                    tooltip={
                      isCommissionKindBonus
                        ? t("commissions.postponed.helpers.nettoPointsChangeDisabledInfo")
                        : t("commissions.postponed.helpers.amountsChangeDisabledInfo")
                    }
                  />
                ) : isCommissionKindBonus ? (
                  t("commissions.batch.helpers.nettoPointAmount")
                ) : (
                  t("commissions.batch.attrs.commission.commissionBaseAmount")
                )
              }
              rules={[isCommissionKindBonus ? validations.notNull : validations.none]}
            >
              <InputNumber
                type="decimal"
                addon={isCommissionKindBonus ? "nettoPoints" : "euro"}
                disabled={formType === "postponedCommission"}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="commissionAmount"
              label={
                formType === "postponedCommission" ? (
                  <LabelWithTooltip
                    label={t("commissions.batch.attrs.commission.commissionAmount")}
                    tooltip={t("commissions.postponed.helpers.amountsChangeDisabledInfo")}
                  />
                ) : (
                  t("commissions.batch.attrs.commission.commissionAmount")
                )
              }
              rules={[validations.notNull]}
            >
              <InputNumber
                type="decimal"
                addon="euro"
                disabled={formType === "postponedCommission" || commissionKind === CommissionKind.BONUS}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="commissionKind"
              label={t("commissions.batch.enums.commissionKind._label")}
              rules={[validations.notNull]}
            >
              <CommissionKindSelect
                onChange={(commissionKind) => {
                  if (commissionKind === CommissionKind.BONUS) {
                    form.setFieldsValue({ commissionAmount: 0 });
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {commission?.calculatedCommissions?.some((calculation) => calculation.modified) && (
        <Alert title={t("commissions.batch.helpers.modifiedCommissionUpdate")} type="warning" showIcon />
      )}
    </Modal>
  );
};
